import { useEffect, useState, DependencyList } from 'react';

import { Auth, Hub } from 'aws-amplify';

import {
  LASTNAME_FIELD,
  FIRSTNAME_FIELD,
  STRIPE_ID_FIELD,
  AUTH_EVENT,
  AUTH_CHANNEL,
  FREE_TRIAL_END_FIELD,
} from 'constants/amplify';

export function isAuthenticatedUser(user: CurrentUser): user is AuthenticatedUser {
  return user.isConnected;
}

type AuthenticatedUser = {
  isConnected: true;
  userInfos: {
    username: string;
    firstname: string;
    lastname: string;
    email: string;
    stripeId: string | null;
    roles: string[];
    freeTrialEnd: string | null;
  };
};
type UnauthenticatedUser = { isConnected: false; userInfos: null };

export type CurrentUser = AuthenticatedUser | UnauthenticatedUser;

export const useUser = (dependency: DependencyList = []) => {
  const [currentUser, setCurrentUser] = useState<CurrentUser>({
    isConnected: false,
    userInfos: null,
  });

  const handleUserChange = () => {
    Auth.currentAuthenticatedUser()
      .then((currentAuthenticatedUser) => {
        setCurrentUser({
          isConnected: true,
          userInfos: {
            username: currentAuthenticatedUser.username,
            email: currentAuthenticatedUser.attributes.email,
            firstname: currentAuthenticatedUser.attributes[FIRSTNAME_FIELD],
            lastname: currentAuthenticatedUser.attributes[LASTNAME_FIELD],
            stripeId: currentAuthenticatedUser.attributes[STRIPE_ID_FIELD],
            freeTrialEnd: currentAuthenticatedUser.attributes[FREE_TRIAL_END_FIELD],
            roles: currentAuthenticatedUser.signInUserSession.accessToken.payload['cognito:groups'],
          },
        });
      })
      .catch(() => {
        setCurrentUser({ isConnected: false, userInfos: null });
      });
  };

  useEffect(() => {
    handleUserChange();

    const authListener = ({ payload }: { payload: any }) => {
      if ([AUTH_EVENT.SIGN_IN, AUTH_EVENT.SIGN_UP, AUTH_EVENT.SIGN_OUT].includes(payload.event)) {
        handleUserChange();
      }
    };

    Hub.listen(AUTH_CHANNEL, authListener);
    return () => Hub.remove(AUTH_CHANNEL, authListener);
  }, dependency);

  return currentUser;
};
